import { parseBucketTimestamp } from 'components/date-range-picker/utc-date-utils'
import { TimeBucketOption } from 'features/charts-v2/types'

/**
 * Centralized timestamp formatting utility
 * This ensures consistent timestamp display across all charts based on the selected time bucket
 */
export class TimestampUtils {
  private static instance: TimestampUtils

  private constructor() {}

  public static getInstance(): TimestampUtils {
    if (!TimestampUtils.instance) {
      TimestampUtils.instance = new TimestampUtils()
    }
    return TimestampUtils.instance
  }

  /**
   * Formats a timestamp string based on the selected time bucket
   * This is the single source of truth for timestamp formatting across all charts
   */
  public formatTimestampForBucket(
    timestamp: string | number | Date | null | undefined,
    timeBucket: TimeBucketOption | null | undefined,
  ): string {
    // Handle null/undefined input
    if (timestamp == null) {
      return 'Unknown'
    }

    // If no time bucket is selected, return the original timestamp as a string
    if (!timeBucket) {
      return typeof timestamp === 'string' ? timestamp : timestamp.toString()
    }

    // Convert to Date object if it's a string. Backend bucket categories are
    // tz-less ISO datetimes; parse them as UTC (not browser-local) so the
    // getUTC* reads below land on the correct bucket for every viewer TZ.
    const date =
      timestamp instanceof Date ? timestamp : parseBucketTimestamp(timestamp)

    if (isNaN(date.getTime())) {
      return String(timestamp)
    }

    // Backend bucket timestamps are UTC boundaries; read them with UTC getters
    // so labels don't shift a bucket for non-UTC viewers (a May bucket would
    // otherwise render as Apr). Matches parseBucketLabelToDateRange below.
    switch (timeBucket) {
      case 'quarter': {
        const quarter = Math.floor(date.getUTCMonth() / 3) + 1
        const year = date.getUTCFullYear()
        return `Q${quarter} ${year}`
      }

      case 'year': {
        return date.getUTCFullYear().toString()
      }

      case 'month': {
        const monthNames = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ]
        const month = monthNames[date.getUTCMonth()]
        const year = date.getUTCFullYear()
        return `${month} ${year}`
      }

      case 'week': {
        const monthNames = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ]
        const month = monthNames[date.getUTCMonth()]
        const day = date.getUTCDate()
        return `${month} ${day}`
      }

      case 'day': {
        const monthNames = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ]
        const month = monthNames[date.getUTCMonth()]
        const day = date.getUTCDate()
        return `${month} ${day}`
      }

      default: {
        // Return original timestamp for unknown time buckets
        // This maintains backward compatibility
        return typeof timestamp === 'string' ? timestamp : timestamp.toString()
      }
    }
  }

  /**
   * Checks if a timestamp should be formatted based on time bucket selection
   * This helps determine when to apply time bucket formatting vs. showing raw values
   */
  public shouldFormatTimestamp(
    timeBucket: TimeBucketOption | null | undefined,
  ): boolean {
    return timeBucket != null
  }

  /**
   * Formats a timestamp for display in data tables
   * Provides consistent formatting between charts and their associated data tables
   */
  public formatForTable(
    timestamp: string | Date,
    timeBucket: TimeBucketOption | null | undefined,
  ): string {
    return this.formatTimestampForBucket(timestamp, timeBucket)
  }

  /**
   * Formats a timestamp for chart axis labels
   * Uses the same logic as general formatting to ensure consistency
   */
  public formatForAxis(
    timestamp: string | Date,
    timeBucket: TimeBucketOption | null | undefined,
  ): string {
    return this.formatTimestampForBucket(timestamp, timeBucket)
  }

  /**
   * Formats a timestamp for tooltips
   * Uses the same logic as general formatting to ensure consistency
   */
  public formatForTooltip(
    timestamp: string | Date,
    timeBucket: TimeBucketOption | null | undefined,
  ): string {
    return this.formatTimestampForBucket(timestamp, timeBucket)
  }

  /**
   * Parses a formatted time bucket label back into a date range
   * Returns the start and end dates for the time bucket
   * Examples:
   * - "Apr 2025" (Month) -> { startDate: Apr 1, 2025 00:00:00, endDate: Apr 30, 2025 23:59:59 }
   * - "Q2 2025" (Quarter) -> { startDate: Apr 1, 2025 00:00:00, endDate: Jun 30, 2025 23:59:59 }
   * - "Jan 15" (Week/Day) -> Requires year context from chart date range or rawTimestamp
   *
   * @param label - The formatted time bucket label (e.g., "Jan 15", "Apr 2025")
   * @param timeBucket - The time bucket type (Day, Week, Month, Quarter, Year)
   * @param chartStartDate - Optional chart start date for year inference (fallback)
   * @param rawTimestamp - Optional raw timestamp from data point payload (preferred)
   */
  public parseBucketLabelToDateRange(
    label: string,
    timeBucket: TimeBucketOption | null | undefined,
    chartStartDate?: Date,
    rawTimestamp?: string | Date | null,
  ): { startDate: Date; endDate: Date } | null {
    if (!timeBucket || !label) {
      return null
    }

    const monthMap: Record<string, number> = {
      Jan: 0,
      Feb: 1,
      Mar: 2,
      Apr: 3,
      May: 4,
      Jun: 5,
      Jul: 6,
      Aug: 7,
      Sep: 8,
      Oct: 9,
      Nov: 10,
      Dec: 11,
    }

    try {
      switch (timeBucket) {
        case 'month': {
          // Format: "Apr 2025"
          const match = label.match(/^(\w+)\s+(\d{4})$/)
          if (!match) return null

          const [, monthName, yearStr] = match
          const month = monthMap[monthName!]
          const year = parseInt(yearStr!, 10)

          if (month === undefined || isNaN(year)) return null

          // UTC bucket boundaries — matches V1 chart's
          // moment.utc(...).startOf('month'/'year'/...) and the chart
          // query's UTC bucket emission. Local-time boundaries shift the
          // window by the user's offset and clip records at the edges
          // (PROD-3157 V2 panel discrepancy).
          const startDate = new Date(Date.UTC(year, month, 1, 0, 0, 0, 0))
          const endDate = new Date(
            Date.UTC(year, month + 1, 0, 23, 59, 59, 999),
          )

          return { startDate, endDate }
        }

        case 'quarter': {
          // Format: "Q2 2025"
          const match = label.match(/^Q(\d)\s+(\d{4})$/)
          if (!match) return null

          const [, quarterStr, yearStr] = match
          const quarter = parseInt(quarterStr!, 10)
          const year = parseInt(yearStr!, 10)

          if (isNaN(quarter) || isNaN(year) || quarter < 1 || quarter > 4) {
            return null
          }

          const startMonth = (quarter - 1) * 3
          const endMonth = startMonth + 2

          const startDate = new Date(Date.UTC(year, startMonth, 1, 0, 0, 0, 0))
          const endDate = new Date(
            Date.UTC(year, endMonth + 1, 0, 23, 59, 59, 999),
          )

          return { startDate, endDate }
        }

        case 'year': {
          // Format: "2025"
          const year = parseInt(label, 10)
          if (isNaN(year)) return null

          const startDate = new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0))
          const endDate = new Date(Date.UTC(year, 11, 31, 23, 59, 59, 999))

          return { startDate, endDate }
        }

        case 'week':
        case 'day': {
          // Format: "Jan 15" (no year specified)
          // Prefer using rawTimestamp if available (most accurate)
          // Otherwise, fall back to chartStartDate for year inference
          const match = label.match(/^(\w+)\s+(\d+)$/)
          if (!match) return null

          const [, monthName, dayStr] = match
          const month = monthMap[monthName!]
          const day = parseInt(dayStr!, 10)

          if (month === undefined || isNaN(day)) return null

          // Strategy 1: Use raw timestamp if provided (most accurate)
          if (rawTimestamp) {
            const rawDate =
              rawTimestamp instanceof Date
                ? rawTimestamp
                : parseBucketTimestamp(rawTimestamp)
            if (!isNaN(rawDate.getTime())) {
              const year = rawDate.getUTCFullYear()

              if (timeBucket === 'day') {
                const startDate = new Date(
                  Date.UTC(year, month, day, 0, 0, 0, 0),
                )
                const endDate = new Date(
                  Date.UTC(year, month, day, 23, 59, 59, 999),
                )
                return { startDate, endDate }
              } else {
                // Week: start from the day, end 6 days later
                const startDate = new Date(
                  Date.UTC(year, month, day, 0, 0, 0, 0),
                )
                const endDate = new Date(
                  Date.UTC(year, month, day + 6, 23, 59, 59, 999),
                )
                return { startDate, endDate }
              }
            }
          }

          // Strategy 2: Fall back to chartStartDate with improved heuristic
          if (!chartStartDate) return null

          // Use the year from chartStartDate as baseline
          let year = chartStartDate.getUTCFullYear()
          const chartMonth = chartStartDate.getUTCMonth()

          // Improved year inference logic:
          // If the label month is significantly earlier than chart start month,
          // it's likely the next year (handles cross-year date ranges)
          // Use a 6-month threshold to be more conservative
          if (month < chartMonth && chartMonth - month > 6) {
            // Chart starts late in year (e.g., Oct, Nov, Dec) and label is early (Jan, Feb, etc.)
            year += 1
          } else if (month > chartMonth && month - chartMonth > 6) {
            // Edge case: chart might have started in previous year
            // (e.g., chart starts Jan 2025 but we're looking at Nov 2024)
            year -= 1
          }

          if (timeBucket === 'day') {
            const startDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0))
            const endDate = new Date(
              Date.UTC(year, month, day, 23, 59, 59, 999),
            )
            return { startDate, endDate }
          } else {
            // Week: start from the day, end 6 days later
            const startDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0))
            const endDate = new Date(
              Date.UTC(year, month, day + 6, 23, 59, 59, 999),
            )
            return { startDate, endDate }
          }
        }

        default:
          return null
      }
    } catch {
      return null
    }
  }
}

// Export singleton instance
export const timestampUtils = TimestampUtils.getInstance()
